import * as React from 'react';
import type { TableProps } from 'antd';
import { Alert, Avatar, Button, Card, Col, Empty, Image, Row, Select, Table, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/plots';
import type { AccountReport } from './types';
import { PLATFORM_LABEL } from './types';
import {
    accountEngagementRate,
    chartGrain,
    chartSeries,
    contentBuckets,
    DEFAULT_POST_SORT,
    engagementRateChartHelp,
    formatCompact,
    formatPct,
    postsOf,
    postMoment,
    sortPosts,
    type PostSortField,
    type PostSortOrder,
    summarise,
    type ChartMetric,
} from './metrics';

const POST_SORT_LABEL: Record<PostSortField, string> = {
    date: 'date',
    post_type: 'type',
    description: 'description',
    views: 'views',
    likes: 'likes',
    comments: 'comments',
    shares: 'shares',
    saves: 'saves',
    engagement_rate: 'engagement rate',
};

const METRICS: { value: ChartMetric; label: string }[] = [
    { value: 'views', label: 'Views' },
    { value: 'likes', label: 'Likes' },
    { value: 'engagement_rate', label: 'Engagement rate' },
    { value: 'posts', label: 'Posts' },
];

const ReportBody: React.FC<{
    report: AccountReport;
    downloading?: boolean;
    onDownload?: () => void;
    headerExtra?: React.ReactNode;
}> = ({ report, downloading, onDownload, headerExtra }) => {
    const posts = postsOf(report);
    const [postSortField, setPostSortField] = React.useState<PostSortField>(DEFAULT_POST_SORT.field);
    const [postSortOrder, setPostSortOrder] = React.useState<PostSortOrder>(DEFAULT_POST_SORT.order);

    const sortedPosts = React.useMemo(
        () => sortPosts(posts, postSortField, postSortOrder),
        [posts, postSortField, postSortOrder],
    );

    const totals = summarise(posts);
    const er = accountEngagementRate(report);
    const allTime = report.period === 'all time' || !report.period;
    const grain = chartGrain(posts, allTime);
    const [metric, setMetric] = React.useState<ChartMetric>('views');
    const series = chartSeries(posts, metric, grain, report.platform);
    const buckets = contentBuckets(posts);
    const maxCount = Math.max(1, ...buckets.map((b) => b.count));
    const maxViews = Math.max(1, ...buckets.map((b) => b.views));
    const top = sortedPosts.slice(0, 8);
    const profile = report.profile;

    const sortOrderFor = (field: PostSortField): 'ascend' | 'descend' | null =>
        (postSortField === field ? postSortOrder : null);

    const onPostsTableChange: TableProps<import('./types').PostMetrics>['onChange'] = (_pag, _filters, sorter) => {
        const active = Array.isArray(sorter) ? sorter[0] : sorter;
        const rawField = active?.field ?? active?.columnKey;
        const field = rawField as PostSortField | undefined;
        if (field && active?.order) {
            setPostSortField(field);
            setPostSortOrder(active.order);
            return;
        }
        setPostSortField(DEFAULT_POST_SORT.field);
        setPostSortOrder(DEFAULT_POST_SORT.order);
    };

    React.useEffect(() => {
        setPostSortField(DEFAULT_POST_SORT.field);
        setPostSortOrder(DEFAULT_POST_SORT.order);
    }, [report.profile_url, report.period, posts.length]);

    const kpis = [
        { label: 'FOLLOWERS', value: formatCompact(profile.followers) },
        { label: 'FOLLOWING', value: formatCompact(profile.following) },
        { label: allTime ? 'POSTS FETCHED' : 'POSTS IN PERIOD', value: String(posts.length) },
        { label: 'POST VIEWS', value: formatCompact(totals.views) },
        { label: 'LIKES', value: formatCompact(totals.likes) },
        { label: 'COMMENTS', value: formatCompact(totals.comments) },
        { label: 'SHARES', value: formatCompact(totals.shares) },
        { label: 'SAVES', value: formatCompact(totals.saves) },
        { label: 'ENGAGEMENT RATE', value: formatPct(er) },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <Avatar size={64} src={profile.image_url || undefined}>
                        {(report.display_name || '?')[0]?.toUpperCase()}
                    </Avatar>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            {report.display_name || report.account_id}
                            {profile.is_verified ? <Tag color="blue" style={{ marginLeft: 8 }}>Verified</Tag> : null}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {PLATFORM_LABEL[report.platform] || report.platform}
                            {' · '}
                            {report.period || 'all time'}
                            {report.profile_url ? (
                                <>
                                    {' · '}
                                    <a href={report.profile_url} target="_blank" rel="noreferrer">Profile</a>
                                </>
                            ) : null}
                        </Typography.Text>
                        <div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Profile stats are a current lifetime snapshot. Post views and likes are the current totals on items published in this period.
                            </Typography.Text>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {headerExtra}
                    {onDownload && (
                        <Button icon={<DownloadOutlined />} loading={downloading} onClick={onDownload}>
                            Download Excel
                        </Button>
                    )}
                </div>
            </div>

            {report.error ? (
                <Typography.Paragraph type="warning" style={{ marginTop: 16 }}>
                    {report.error}
                </Typography.Paragraph>
            ) : null}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 12,
                marginTop: 20,
            }}>
                {kpis.map((item) => (
                    <Card size="small" key={item.label}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 0.4 }}>{item.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>{item.value}</div>
                    </Card>
                ))}
            </div>

            <Card
                size="small"
                title="Performance over time"
                style={{ marginTop: 16 }}
                extra={(
                    <Select
                        size="small"
                        value={metric}
                        onChange={setMetric}
                        options={METRICS}
                        style={{ width: 168 }}
                    />
                )}
            >
                {metric === 'engagement_rate' ? (
                    <Alert
                        type="info"
                        showIcon
                        message="How engagement rate is calculated"
                        description={engagementRateChartHelp(report.platform)}
                        style={{ marginBottom: 12 }}
                    />
                ) : null}
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {grain === 'week' ? 'Grouped by week' : 'Grouped by publish day'}
                </Typography.Text>
                {series.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dated posts" /> : (
                    <Line
                        data={series}
                        xField="date"
                        yField="value"
                        height={280}
                        smooth={false}
                        color="#1677ff"
                        point={{ size: 3 }}
                        yAxis={{
                            min: 0,
                            label: metric === 'engagement_rate'
                                ? { formatter: (v: string) => formatPct(Number(v)) }
                                : undefined,
                        }}
                        tooltip={metric === 'engagement_rate' ? {
                            formatter: (datum: { value?: number }) => ({
                                name: 'Engagement rate',
                                value: formatPct(datum.value ?? 0),
                            }),
                        } : undefined}
                        xAxis={{
                            tickCount: 8,
                            label: { autoHide: true, autoRotate: false },
                        }}
                    />
                )}
            </Card>

            <Card size="small" title="Content types" style={{ marginTop: 16 }}>
                {buckets.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                    <Row gutter={24}>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>POSTS</div>
                            {buckets.map((b) => (
                                <BarRow key={b.type} label={b.type} value={b.count} max={maxCount} />
                            ))}
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>VIEWS</div>
                            {buckets.map((b) => (
                                <BarRow key={b.type} label={b.type} value={b.views} max={maxViews} compact />
                            ))}
                        </Col>
                    </Row>
                )}
            </Card>

            <Typography.Title level={5} style={{ marginTop: 24 }}>Top posts</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, marginTop: -8 }}>
                Top 8 by {POST_SORT_LABEL[postSortField]} ({postSortOrder === 'descend' ? 'highest first' : 'lowest first'}).
            </Typography.Text>
            {top.length === 0 ? <Empty description="No posts in this period" /> : (
                <Row gutter={[12, 12]}>
                    {top.map((post) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={post.post_id || post.post_url}>
                            <PostCard post={post} />
                        </Col>
                    ))}
                </Row>
            )}

            <Typography.Title level={5} style={{ marginTop: 24 }}>All posts</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, marginTop: -8 }}>
                Click a column header to sort ascending or descending.
            </Typography.Text>
            <Table
                rowKey={(row) => row.post_id || row.post_url}
                dataSource={sortedPosts}
                onChange={onPostsTableChange}
                showSorterTooltip={{ target: 'sorter-icon' }}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: 900 }}
                columns={[
                    {
                        title: 'Date',
                        key: 'date',
                        sorter: (a, b) => (postMoment(a)?.valueOf() ?? 0) - (postMoment(b)?.valueOf() ?? 0),
                        sortOrder: sortOrderFor('date'),
                        render: (_, row) => {
                            const at = postMoment(row);
                            return at ? at.format('YYYY-MM-DD') : '—';
                        },
                    },
                    {
                        title: 'Type',
                        dataIndex: 'post_type',
                        key: 'post_type',
                        sorter: (a, b) => (a.post_type || '').localeCompare(b.post_type || ''),
                        sortOrder: sortOrderFor('post_type'),
                        render: (v: string) => v || '—',
                    },
                    {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                        sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
                        sortOrder: sortOrderFor('description'),
                        render: (v: string, row) => row.post_url
                            ? <a href={row.post_url} target="_blank" rel="noreferrer">{v || row.post_id}</a>
                            : (v || '—'),
                    },
                    {
                        title: 'Views',
                        dataIndex: 'views',
                        key: 'views',
                        sorter: (a, b) => (a.views ?? 0) - (b.views ?? 0),
                        sortOrder: sortOrderFor('views'),
                        render: (v: number) => formatCompact(v),
                    },
                    {
                        title: 'Likes',
                        dataIndex: 'likes',
                        key: 'likes',
                        sorter: (a, b) => (a.likes ?? 0) - (b.likes ?? 0),
                        sortOrder: sortOrderFor('likes'),
                        render: (v: number) => formatCompact(v),
                    },
                    {
                        title: 'Comments',
                        dataIndex: 'comments',
                        key: 'comments',
                        sorter: (a, b) => (a.comments ?? 0) - (b.comments ?? 0),
                        sortOrder: sortOrderFor('comments'),
                        render: (v: number) => formatCompact(v),
                    },
                    {
                        title: 'Shares',
                        dataIndex: 'shares',
                        key: 'shares',
                        sorter: (a, b) => (a.shares ?? 0) - (b.shares ?? 0),
                        sortOrder: sortOrderFor('shares'),
                        render: (v: number) => formatCompact(v),
                    },
                    {
                        title: 'Saves',
                        dataIndex: 'saves',
                        key: 'saves',
                        sorter: (a, b) => (a.saves ?? 0) - (b.saves ?? 0),
                        sortOrder: sortOrderFor('saves'),
                        render: (v: number) => formatCompact(v),
                    },
                    {
                        title: 'ER',
                        dataIndex: 'engagement_rate',
                        key: 'engagement_rate',
                        sorter: (a, b) => (a.engagement_rate ?? 0) - (b.engagement_rate ?? 0),
                        sortOrder: sortOrderFor('engagement_rate'),
                        render: (v: number) => formatPct(v),
                    },
                ]}
            />
        </div>
    );
};

const BarRow: React.FC<{ label: string; value: number; max: number; compact?: boolean }> = ({ label, value, max, compact }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 110, fontSize: 12, textTransform: 'capitalize' }}>{label}</div>
        <div style={{ flex: 1, height: 16, background: '#f5f5f5' }}>
            <div style={{ width: `${(value / max) * 100}%`, height: '100%', background: '#111' }} />
        </div>
        <span style={{ width: 56, textAlign: 'right' }}>{compact ? formatCompact(value) : value}</span>
    </div>
);

const PostCard: React.FC<{ post: import('./types').PostMetrics }> = ({ post }) => {
    const [broken, setBroken] = React.useState(false);
    const at = postMoment(post);
    return (
        <Card size="small" style={{ borderRadius: 12, overflow: 'hidden', height: '100%' }} bodyStyle={{ padding: 12 }}>
            <div style={{
                background: '#f5f5f5',
                borderRadius: 8,
                minHeight: 160,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                marginBottom: 8,
            }}>
                {post.media_url && !broken ? (
                    <Image
                        src={post.media_url}
                        alt=""
                        style={{ width: '100%', objectFit: 'cover', maxHeight: 220 }}
                        preview={false}
                        onError={() => setBroken(true)}
                    />
                ) : (
                    <Typography.Text type="secondary">{post.post_type || 'Post'}</Typography.Text>
                )}
            </div>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, marginBottom: 8, minHeight: 36 }}>
                {post.description || '—'}
            </Typography.Paragraph>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {at ? at.format('MMM D, YYYY') : ''}
                {post.post_type ? ` · ${post.post_type}` : ''}
            </Typography.Text>
            <div style={{ marginTop: 8, fontSize: 12 }}>
                {formatCompact(post.views)} views · {formatCompact(post.likes)} likes · {formatPct(post.engagement_rate)}
            </div>
            {post.post_url ? (
                <div style={{ marginTop: 8 }}>
                    <a href={post.post_url} target="_blank" rel="noreferrer">Open</a>
                </div>
            ) : null}
        </Card>
    );
};

export default ReportBody;
